import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import axios from "axios";
import { initialPasses, currentUser } from "../mock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PassesContext = createContext(null);

export function PassesProvider({ children }) {
  const [passes, setPasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seeded, setSeeded] = useState(false);

  // Load passes from backend on mount
  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/passes`);
      setPasses(res.data || []);
      return res.data || [];
    } catch (e) {
      console.error("Failed to load passes", e);
      return [];
    }
  }, []);

  // Seed initial mock passes if DB is empty (one-time)
  const maybeSeed = useCallback(async () => {
    try {
      const list = await refresh();
      if (list.length === 0 && !seeded && !localStorage.getItem("oht_seeded")) {
        // Seed sequentially to preserve order
        for (const p of initialPasses.slice(0, 30)) {
          await axios.post(`${API}/passes`, {
            talmidId: p.talmidId,
            talmidName: p.talmidName,
            fatherName: p.fatherName,
            klasId: p.klasId,
            klasName: p.klasName,
            date: p.date,
            time: p.time,
            reason: p.reason,
            signedBy: p.signedBy,
          });
        }
        localStorage.setItem("oht_seeded", "1");
        setSeeded(true);
        await refresh();
      }
    } catch (e) {
      console.error("Seeding failed", e);
    } finally {
      setLoading(false);
    }
  }, [refresh, seeded]);

  useEffect(() => {
    maybeSeed();
    // eslint-disable-next-line
  }, []);

  const addPass = useCallback(async (data) => {
    const payload = {
      talmidId: data.talmidId,
      talmidName: data.talmidName,
      fatherName: data.fatherName,
      klasId: data.klasId,
      klasName: data.klasName,
      date: data.date || new Date().toISOString().slice(0, 10),
      time: data.time || new Date().toTimeString().slice(0, 5),
      reason: data.reason,
      reasonCategory: data.reasonCategory || null,
      signedBy: data.signedBy || currentUser.name,
    };
    const res = await axios.post(`${API}/passes`, payload);
    const created = res.data;
    setPasses((prev) => [created, ...prev]);
    return created;
  }, []);

  const getPass = useCallback(
    async (id) => {
      const inCache = passes.find((p) => p.id === id);
      if (inCache) return inCache;
      try {
        const res = await axios.get(`${API}/passes/${id}`);
        return res.data;
      } catch {
        return null;
      }
    },
    [passes]
  );

  const deletePass = useCallback(async (id) => {
    await axios.delete(`${API}/passes/${id}`);
    setPasses((prev) => prev.filter((p) => p.id !== id));
  }, []);

  return (
    <PassesContext.Provider value={{ passes, loading, addPass, getPass, deletePass, refresh }}>
      {children}
    </PassesContext.Provider>
  );
}

export function usePasses() {
  const ctx = useContext(PassesContext);
  if (!ctx) throw new Error("usePasses must be used within PassesProvider");
  return ctx;
}
